import { useEffect, useId, useRef, useState } from "react";
import { ApiError, api, type DocumentDraft, type TravelDocument, type TripFile, type TripView } from "./api.ts";
import { AttachFile, FileList, MAX_FILE_BYTES, size } from "./Files.tsx";
import { useGrow } from "./hooks.ts";
import { useJustDone } from "./justDone.ts";
import { useKept, useOnline } from "./offline.ts";
import { ReportThis } from "./ReportThis.tsx";
import { useReadOnly } from "./readOnly.ts";
import { Button, Chip, Icon, SegmentedControl } from "./souvenir.ts";
import { factLine } from "./ui.tsx";
import { useViewing } from "./viewing.ts";

// Travel documents, on Bookings (DESIGN.md, Records, "Document (personal)", and "Private to the app, never to an AI";
// features/documents.md): kept in the app only. Each person adds, edits, shares and removes their own here, and
// uploads their files straight from the device; no AI reads or writes them. Everyone in the trip sees each one's
// kind, name, status and expiry, the app flagging one that runs out before the trip ends; its details and files only
// its owner and those it's shared with. Every change is one write signed "on the web", undoable at once (the trip's
// notice) and from Updates.

// The kinds offered, as stored; anything else is the person's own word (Other).
const KINDS = [
  { value: "passport", label: "Passport" },
  { value: "visa", label: "Visa" },
  { value: "insurance", label: "Insurance" },
  { value: "licence", label: "Licence" },
  { value: "vaccination", label: "Vaccination" },
] as const;
const OTHER = "other";
const MAX_TITLE = 200;
const MAX_KIND = 40;
const MAX_STATUS = 60;
const MAX_DETAILS = 30;
const MAX_LABEL = 60;
const MAX_VALUE = 500;
const isDate = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d);
const DAY = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
const fmtDate = (date: string) => DAY.format(new Date(`${date}T00:00:00Z`));

// The trip's last day, for flagging a document that runs out before it ends; null while it has no dates.
function tripEnd(trip: TripView): string | null {
  const d = trip.dates;
  if (d.precision === "days") return d.end;
  if (d.precision === "day") return d.day;
  return null;
}

// A detail's label as the row and the form say it ("issued_by": "Issued by").
const labelOf = (key: string) => {
  const words = key.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};
const NOTES = "notes";

// A document's details in the order they're read: as kept (Postgres keeps an object's keys in its own order), notes
// last.
export function detailsInOrder(details: TravelDocument["details"]): [string, string | number | boolean][] {
  const all = Object.entries(details ?? {});
  return [...all.filter(([k]) => k.toLowerCase() !== NOTES), ...all.filter(([k]) => k.toLowerCase() === NOTES)];
}

// A document's details as the form holds them: one a line, "Number: E1234567".
function detailsText(details: TravelDocument["details"]): string {
  return detailsInOrder(details)
    .map(([k, v]) => `${labelOf(k)}: ${String(v)}`)
    .join("\n");
}

// The details back from the form's lines. Each "Label: value" line is a detail (a link's "https:" is never a label);
// any other line goes into notes. A label said as a detail's was keeps its key, and an unchanged value its type.
export function detailsOf(
  text: string,
  before: NonNullable<TravelDocument["details"]> = {},
): { details: Record<string, string | number | boolean>; problem: string | null } {
  const details: Record<string, string | number | boolean> = {};
  const loose: string[] = [];
  const keyOf = (label: string) =>
    Object.keys(before).find((k) => labelOf(k).toLowerCase() === label.toLowerCase()) ?? label;
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const at = line.indexOf(":");
    const label = at > 0 ? line.slice(0, at).trim() : "";
    if (!label || /^(https?|ftp|mailto|tel)$/i.test(label)) {
      loose.push(line);
      continue;
    }
    if (label.length > MAX_LABEL) return { details, problem: `A label is ${MAX_LABEL} characters at most.` };
    const value = line.slice(at + 1).trim();
    if (!value) return { details, problem: `Say what ${label} is, after its colon.` };
    const key = keyOf(label);
    const was = before[key];
    details[key] = was !== undefined && String(was) === value ? was : value;
  }
  if (loose.length) {
    const key = Object.keys(details).find((k) => k.toLowerCase() === NOTES) ?? keyOf(NOTES);
    details[key] = [details[key], ...loose].filter((v) => v !== undefined).join("; ");
  }
  if (Object.keys(details).length > MAX_DETAILS) return { details, problem: `${MAX_DETAILS} details at most.` };
  const long = Object.entries(details).find(([, v]) => String(v).length > MAX_VALUE);
  if (long) return { details, problem: `${labelOf(long[0])} is ${MAX_VALUE} characters at most.` };
  return { details, problem: null };
}

// Travel documents: the rows, and for anyone in the trip (not on a read-only trip or a shared trip's view), Add a
// travel document at the foot; `start`: Bookings' Add asked for it.
export function TravelDocuments({
  trip,
  documents,
  files,
  attach,
  start = false,
  onStarted,
  onChanged,
}: {
  trip: TripView;
  documents: TravelDocument[];
  // Files on documents (only those this person may see).
  files: TripFile[];
  // From an AI's upload link before the rule: the document to attach a file to.
  attach: string | null;
  start?: boolean;
  onStarted?: (() => void) | undefined;
  onChanged: () => void;
}) {
  const readOnly = useReadOnly();
  const viewing = useViewing();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const addAt = useRef<HTMLDivElement>(null);
  const [refocus, setRefocus] = useState<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: once for each Add a travel document asked from Add
  useEffect(() => {
    if (!start) return;
    setAdding(true);
    onStarted?.();
  }, [start]);
  // The one being edited, removed (here, or on another device): its form goes with it.
  useEffect(() => {
    if (editing && !documents.some((d) => d.id === editing)) {
      setEditing(null);
      setRefocus("add");
    }
  }, [editing, documents]);
  // Closed, back where it was opened from: Add a travel document, or the row's Edit.
  useEffect(() => {
    if (!refocus || adding || editing) return;
    setRefocus(null);
    const at =
      refocus === "add"
        ? addAt.current?.querySelector<HTMLElement>("button")
        : document.querySelector<HTMLElement>(`[data-document="${CSS.escape(refocus)}"] [data-edit="document"]`);
    at?.focus({ preventScroll: true });
  }, [refocus, adding, editing]);
  const edits = !readOnly && !viewing;
  return (
    <>
      {documents.length > 0 && (
        <ul className="tm-rows">
          {documents.map((d) =>
            editing === d.id ? (
              <li key={d.id}>
                <DocumentForm
                  trip={trip}
                  doc={d}
                  onChanged={onChanged}
                  onClose={() => {
                    setEditing(null);
                    setRefocus(d.id);
                  }}
                />
              </li>
            ) : (
              <DocumentRow
                key={d.id}
                doc={d}
                trip={trip}
                files={files.filter((f) => f.on === d.id)}
                invited={attach === d.id}
                onEdit={edits && d.version !== undefined && !adding ? () => setEditing(d.id) : null}
                onChanged={onChanged}
              />
            ),
          )}
        </ul>
      )}
      {/* One form at a time: while one of mine is being edited, nothing to add. */}
      {edits && editing === null && (
        <div className="tm-add-todo-at tm-add-document-at" ref={addAt}>
          {adding ? (
            <DocumentForm
              trip={trip}
              doc={null}
              onChanged={onChanged}
              onClose={() => {
                setAdding(false);
                setRefocus("add");
              }}
            />
          ) : (
            <Button variant="secondary" size="sm" icon="plus" data-edit="add-document" onClick={() => setAdding(true)}>
              Add a travel document
            </Button>
          )}
        </div>
      )}
    </>
  );
}

function DocumentRow({
  doc,
  trip,
  files,
  invited,
  onEdit,
  onChanged,
}: {
  doc: TravelDocument;
  trip: TripView;
  files: TripFile[];
  invited: boolean;
  // Mine, on a trip that can change: Edit opens its form in its place.
  onEdit: (() => void) | null;
  onChanged: () => void;
}) {
  const mine = doc.person.id === trip.you;
  const end = tripEnd(trip);
  const runsOut = doc.expires && end && isDate(end) && doc.expires <= end;
  const details = detailsInOrder(doc.details);
  const edit =
    mine && onEdit ? (
      <Button
        variant="quiet"
        size="sm"
        type="button"
        icon="note"
        data-edit="document"
        aria-label={`Edit ${doc.title}`}
        onClick={onEdit}
      >
        Edit
      </Button>
    ) : null;
  return (
    <li data-document={doc.id}>
      <div className="tm-row tm-row-static tm-document">
        <span className="tm-row-icon">
          <Icon name="book" size={18} />
        </span>
        <span className="tm-row-text">
          <span className="callout">{doc.title}</span>
          <span className="caption tm-muted">
            {factLine([
              mine ? "Yours" : doc.person.name,
              doc.kind,
              doc.status,
              doc.expires ? `expires ${fmtDate(doc.expires)}` : null,
            ])}
          </span>
          {runsOut && <span className="caption tm-error">Expires before the trip ends</span>}
          {details.length > 0 && (
            <span className="caption tm-document-details">
              {factLine(details.map(([k, v]) => `${labelOf(k)}: ${String(v)}`))}
            </span>
          )}
          {doc.details_hidden && <span className="caption tm-muted">Details private to {doc.person.name}</span>}
          {mine && doc.shared_with !== undefined && (
            <span className="caption tm-muted">
              {doc.shared_with === "everyone"
                ? "Details shared with everyone"
                : doc.shared_with.length
                  ? `Details shared with ${doc.shared_with
                      .map((id) => trip.members.find((m) => m.id === id)?.name ?? "someone")
                      .join(", ")}`
                  : "Details only you see"}
            </span>
          )}
          {/* Files on it: the owner's to add and remove; the trip sees them where the details are shared. */}
          <FileList files={files} trip={trip.id} canRemove={mine} onChanged={onChanged} />
          {mine ? (
            <AttachFile
              trip={trip.id}
              on={doc.id}
              invited={invited ? "file" : null}
              more={edit}
              onChanged={onChanged}
            />
          ) : null}
        </span>
      </div>
    </li>
  );
}

type Sharing = "me" | "everyone" | "some";

// Add a travel document (`doc` null), or edit one of mine: its kind (the usual ones, or Other in my own word), its
// name, status and expiry (the whole trip sees these), its details (one a line, only for me and those I share them
// with), who those are, and, adding, its files from this device. Editing, Remove takes it away (undoable).
export function DocumentForm({
  trip,
  doc,
  onChanged,
  onClose,
}: {
  trip: TripView;
  doc: TravelDocument | null;
  onChanged: () => void;
  onClose: () => void;
}) {
  const id = useId();
  const say = useJustDone();
  const online = useOnline();
  const kept = useKept();
  const offline = !online || kept !== null;
  const known = KINDS.find((k) => k.value === doc?.kind);
  const [kind, setKind] = useState<string>(doc ? (known ? known.value : OTHER) : "");
  const [otherKind, setOtherKind] = useState(doc && !known ? doc.kind : "");
  const [title, setTitle] = useState(doc?.title ?? "");
  // The name the kind filled in, while it's still that: picking another kind changes it too.
  const [named, setNamed] = useState<string | null>(null);
  const [status, setStatus] = useState(doc?.status ?? "");
  const [expires, setExpires] = useState(doc?.expires ?? "");
  const [details, setDetails] = useState(detailsText(doc?.details));
  const growDetails = useGrow(details);
  const shared = doc?.shared_with;
  const [sharing, setSharing] = useState<Sharing>(
    shared === "everyone" ? "everyone" : shared && shared.length > 0 ? "some" : "me",
  );
  const others = trip.members.filter((m) => m.id !== trip.you);
  const [picked, setPicked] = useState<string[]>(Array.isArray(shared) ? shared : []);
  const [picks, setPicks] = useState<File[]>([]);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"save" | "remove" | null>(null);
  // Saved at this version: closed once the list has it (or after a while all the same), so the row never shows it as
  // it was. Removed: the form goes with its row (TravelDocuments).
  const [saved, setSaved] = useState<number | null>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const gone = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (gone.current) clearTimeout(gone.current);
    },
    [],
  );
  useEffect(() => {
    if (saved === null) return;
    if ((doc?.version ?? 0) >= saved) return close.current();
    const timer = setTimeout(() => close.current(), 5000);
    return () => clearTimeout(timer);
  }, [saved, doc?.version]);
  const form = useRef<HTMLFormElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // Opened: its top in view (clear of the trip's bar on a phone: its scroll margin), its first field taking the focus.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, as it opens
  useEffect(() => {
    form.current?.querySelector<HTMLElement>(doc ? "input:not([type=file])" : "button")?.focus({ preventScroll: true });
    form.current?.scrollIntoView({ block: "start" });
  }, []);

  const pickKind = (value: string) => {
    setKind(value);
    setProblems(({ kind: _, ...rest }) => rest);
    const label = KINDS.find((k) => k.value === value)?.label ?? null;
    // An empty name, or still the one the last kind gave, follows the kind.
    if (label && (!title.trim() || title === named)) {
      setTitle(label);
      setNamed(label);
    }
    if (value === OTHER && title === named) {
      setTitle("");
      setNamed(null);
    }
  };

  const save = async () => {
    if (offline || busy) return;
    const wrong: Record<string, string> = {};
    const kindSaid = kind === OTHER ? otherKind.trim() : kind;
    if (!kind) wrong["kind"] = "Which kind of document?";
    else if (!kindSaid) wrong["kind"] = "Say which kind.";
    const name = title.trim();
    if (!name) wrong["title"] = "Name it: Passport, Korea K-ETA…";
    if (expires && !isDate(expires)) wrong["expires"] = "A whole date, or none.";
    const said = detailsOf(details, doc?.details ?? {});
    if (said.problem) wrong["details"] = said.problem;
    if (sharing === "some" && picked.length === 0) wrong["sharing"] = "Pick who sees them, or choose Only you.";
    setProblems(wrong);
    const first = Object.keys(wrong)[0];
    if (first) {
      form.current?.querySelector<HTMLElement>(`[data-field="${first}"]`)?.focus();
      return;
    }
    const draft: DocumentDraft = {
      kind: kindSaid,
      title: name,
      status: status.trim() || null,
      expires: expires || null,
      details: said.details,
      shared_with: sharing === "everyone" ? "everyone" : sharing === "some" ? picked : [],
    };
    setError(null);
    setBusy("save");
    try {
      if (doc) {
        const { change, version } = await api.changeDocument(trip.id, doc.id, { ...draft, version: doc.version ?? 0 });
        onChanged();
        say({ change, text: `Saved ${name}` });
        setSaved(version);
        return;
      }
      const { id: made, change } = await api.addDocument(trip.id, draft);
      // Its files, straight from this device, once it's there; one that doesn't go in is said, the rest kept.
      const failed: string[] = [];
      for (const f of picks) {
        try {
          await api.attachFile(trip.id, made, f);
        } catch (e) {
          failed.push(`${f.name} didn't attach (${e instanceof Error ? e.message : "try again"})`);
        }
      }
      onChanged();
      say({ change, text: [`Added ${name}`, ...failed].join("; ") });
      onClose();
    } catch (e) {
      setBusy(null);
      if (e instanceof ApiError && e.status === 409) {
        onChanged();
        setError("It changed on another device while this was open, so nothing was saved. Save again to keep this.");
      } else setError(e instanceof Error ? e.message : "Couldn't save it. Try again.");
    }
  };

  const remove = async () => {
    if (!doc || offline || busy) return;
    setError(null);
    setBusy("remove");
    try {
      const { change } = await api.removeDocument(trip.id, doc.id);
      onChanged();
      say({ change, text: `Removed ${doc.title}` });
      // Gone with its row once the list has it gone; after a while all the same.
      gone.current = setTimeout(() => close.current(), 5000);
    } catch (e) {
      setBusy(null);
      setError(e instanceof Error ? e.message : "Couldn't remove it. Try again.");
    }
  };

  const choose = (list: FileList | null) => {
    const chosen = [...(list ?? [])];
    if (fileInput.current) fileInput.current.value = "";
    const tooBig = chosen.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) {
      setProblems((p) => ({ ...p, files: `${tooBig.name} is over 20 MB. Try a smaller one.` }));
      return;
    }
    setProblems(({ files: _, ...rest }) => rest);
    setPicks((p) => [...p, ...chosen]);
  };

  const problem = (field: string) => problems[field] ?? null;
  const field = (name: string) => `sv-field tm-booking-field${problem(name) ? " is-wrong" : ""}`;
  return (
    <form
      ref={form}
      className="tm-edit-form tm-document-form"
      aria-labelledby={`${id}-title`}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }}
    >
      <h2 id={`${id}-title`} className="title">
        {doc ? `Edit ${doc.title}` : "Add a travel document"}
      </h2>
      <fieldset className="tm-document-kinds" data-field="kind" tabIndex={-1}>
        <legend className="sv-field-label">Kind</legend>
        <div className="tm-booking-picks">
          {[...KINDS, { value: OTHER, label: "Other" }].map((k) => (
            <Chip key={k.value} selected={kind === k.value} onClick={() => pickKind(k.value)}>
              {k.label}
            </Chip>
          ))}
        </div>
        {kind === OTHER && (
          <div className={field("kind")}>
            <label className="sv-field-label tm-hidden" htmlFor={`${id}-kind`}>
              Which kind
            </label>
            <input
              id={`${id}-kind`}
              className="sv-field-input"
              value={otherKind}
              maxLength={MAX_KIND}
              autoComplete="off"
              placeholder="Entry permit, health form…"
              aria-invalid={problem("kind") ? true : undefined}
              onChange={(e) => {
                setOtherKind(e.target.value);
                setProblems(({ kind: _, ...rest }) => rest);
              }}
            />
          </div>
        )}
        {problem("kind") && <span className="sv-field-hint tm-error">{problem("kind")}</span>}
      </fieldset>
      <div className={field("title")}>
        <label className="sv-field-label" htmlFor={`${id}-name`}>
          Name
        </label>
        <input
          id={`${id}-name`}
          data-field="title"
          className="sv-field-input"
          value={title}
          maxLength={MAX_TITLE}
          autoComplete="off"
          placeholder="Passport, Korea K-ETA…"
          aria-invalid={problem("title") ? true : undefined}
          onChange={(e) => {
            setTitle(e.target.value);
            setNamed(null);
            setProblems(({ title: _, ...rest }) => rest);
          }}
        />
        {problem("title") && <span className="sv-field-hint tm-error">{problem("title")}</span>}
      </div>
      <div className="tm-add-todo-fields">
        <div className="sv-field tm-booking-field">
          <label className="sv-field-label" htmlFor={`${id}-status`}>
            Status
          </label>
          <input
            id={`${id}-status`}
            className="sv-field-input"
            value={status}
            maxLength={MAX_STATUS}
            autoComplete="off"
            placeholder="Valid, applied…"
            onChange={(e) => setStatus(e.target.value)}
          />
        </div>
        <div className={field("expires")}>
          <label className="sv-field-label" htmlFor={`${id}-expires`}>
            Expires
          </label>
          <input
            id={`${id}-expires`}
            data-field="expires"
            className={`sv-field-input${expires ? "" : " is-empty"}`}
            type="date"
            value={expires}
            aria-invalid={problem("expires") ? true : undefined}
            onChange={(e) => setExpires(e.target.value)}
          />
          {problem("expires") && <span className="sv-field-hint tm-error">{problem("expires")}</span>}
        </div>
      </div>
      <p className="caption tm-muted">Everyone in the trip sees its kind, name, status and expiry.</p>
      <div className={field("details")}>
        <label className="sv-field-label" htmlFor={`${id}-details`}>
          Details
        </label>
        <textarea
          ref={growDetails}
          id={`${id}-details`}
          data-field="details"
          className="sv-field-input tm-document-details-input"
          value={details}
          rows={3}
          placeholder={"Number: E1234567\nIssued by: Singapore"}
          aria-invalid={problem("details") ? true : undefined}
          aria-describedby={`${id}-details-hint`}
          onChange={(e) => {
            setDetails(e.target.value);
            setProblems(({ details: _, ...rest }) => rest);
          }}
        />
        <span id={`${id}-details-hint`} className={`sv-field-hint${problem("details") ? " tm-error" : ""}`}>
          {problem("details") ?? "One a line, its label first. Only you and those you share them with see these."}
        </span>
      </div>
      <fieldset className="tm-document-sharing" data-field="sharing" tabIndex={-1}>
        <legend className="sv-field-label">Who sees the details and files</legend>
        <SegmentedControl
          label="Who sees the details and files"
          value={sharing}
          onChange={(v) => {
            setSharing(v as Sharing);
            setProblems(({ sharing: _, ...rest }) => rest);
          }}
          options={[
            { value: "me", label: "Only you" },
            { value: "everyone", label: "Everyone" },
            ...(others.length > 0 ? [{ value: "some", label: "Some people" }] : []),
          ]}
        />
        {sharing === "some" && (
          <ul className="tm-document-people">
            {others.map((m) => (
              <li key={m.id}>
                <label className="tm-document-person">
                  <input
                    type="checkbox"
                    checked={picked.includes(m.id)}
                    onChange={(e) => {
                      setPicked((p) => (e.target.checked ? [...p, m.id] : p.filter((x) => x !== m.id)));
                      setProblems(({ sharing: _, ...rest }) => rest);
                    }}
                  />
                  <span className="callout">{m.name}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
        {problem("sharing") && <span className="sv-field-hint tm-error">{problem("sharing")}</span>}
      </fieldset>
      {!doc && (
        <div className="tm-document-files" data-field="files" tabIndex={-1}>
          <span className="sv-field-label">Files</span>
          {picks.length > 0 && (
            <ul className="tm-document-picks" aria-label="Files to attach">
              {picks.map((f, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: the files chosen, in order; the same one twice is two
                <li key={`${f.name}-${i}`} className="tm-document-pick">
                  {/* As a file on a card shows: a picture, or a page. */}
                  <Icon name={f.type.startsWith("image/") ? "photos" : "book"} size={18} />
                  <span className="callout tm-document-pick-name">{f.name}</span>
                  <span className="caption tm-muted">{size(f.size)}</span>
                  <button
                    type="button"
                    className="tm-fact-remove"
                    aria-label={`Don't attach ${f.name}`}
                    onClick={() => setPicks((p) => p.filter((_, j) => j !== i))}
                  >
                    <Icon name="close" size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,.heic"
            multiple
            hidden
            aria-label="Choose a file to attach"
            onChange={(e) => choose(e.target.files)}
          />
          <span className="tm-document-choose">
            <Button variant="quiet" size="sm" type="button" icon="plus" onClick={() => fileInput.current?.click()}>
              {picks.length ? "Choose another file" : "Choose a file"}
            </Button>
          </span>
          <span className={`sv-field-hint${problem("files") ? " tm-error" : ""}`}>
            {problem("files") ?? "A scan or a photo of it: a PDF or an image, up to 20 MB. Seen as its details are."}
          </span>
        </div>
      )}
      {offline && (
        <p className="callout tm-booking-note" role="status">
          <Icon name="cloud" size={20} className="tm-booking-icon" />
          <span>No signal: saving needs signal.</span>
        </p>
      )}
      {error && (
        <p className="caption tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
      <span className="tm-note-actions">
        <Button variant="primary" size="sm" type="submit" disabled={busy !== null || offline}>
          {busy === "save" ? (doc ? "Saving…" : "Adding…") : doc ? "Save" : "Add document"}
        </Button>
        <Button variant="quiet" size="sm" type="button" onClick={onClose}>
          Cancel
        </Button>
        {doc && (
          <Button
            variant="quiet"
            size="sm"
            type="button"
            className="tm-document-remove"
            disabled={busy !== null || offline}
            onClick={remove}
          >
            {busy === "remove" ? "Removing…" : "Remove"}
          </Button>
        )}
      </span>
    </form>
  );
}
